import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  Row, Col, Form, Divider, DatePicker, Typography, Spin, Select,
} from 'antd';
import { DateTime } from 'luxon';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

// Import Components:
import FormCurrencyInput from '../../common/inputs/FormCurrencyInput';
import FormTextInput from '../../common/inputs/FormTextInput';
import OnTraccrSimpleDatePicker from '../../common/inputs/OnTraccrSimpleDatePicker';
import ContactSelector from '../../common/inputs/ContactSelector';
import FormTreeSelectInput from '../../common/inputs/FormTreeSelectInput';

import InvoiceAmountDistributor from './InvoiceAmountDistributor';
import InvoiceStatusSelector from './InvoiceStatusSelector';
import FileDestinationSelect from './FileDestinationSelect';

// Import Helpers/Constants
import { PAYABLE_FORM_TYPES } from '../payables.constants';
import { getFormattedCurrency, getFormattedDate } from '../../forms/formHelpers';
import { INVOICE_PROP } from './invoiceConstants';
import { getIdMap, sortByNumber } from '../../helpers/helpers';
import {
  getValueFormsQuery,
  getValueFormTitle,
} from './invoiceHelpers';

import { getForms } from '../../forms/state/forms.actions';
import DisplayText from '../../common/text/DisplayText';
import DivisionSelector from '../../common/inputs/DivisionSelector';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import { TAX_CALCULATION_OPTIONS } from '../../settings/Integrations/integrationHelpers';
import { getQuickbooksIntegrations } from '../../settings/state/settings.actions';
import OnTraccrNumberInput from '../../common/inputs/OnTraccrNumberInput';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const TypeOptions = [
  { label: 'Expense', value: 'purchase' },
  { label: 'Bill', value: 'bill' },
];

const taxPercentageKey = 'InvoiceAddForm-taxPercentage';
const INVOICE_NOTES_MAX_LENGTH = 500;

/** Invoice Add/Edit Form Component */
function InvoiceAddForm({
  formRef,
  formId, // is defined when we access this component through form detail slider
  invoice: {
    id: invoiceId,
    formId: invoiceFormId,
    invoiceNumber,
    status: invoiceStatus,
    amount: invoiceAmount,
    description: invoiceDescription,
    notes: invoiceNotes,
    dateIssued: invoiceDateIssued,
    dueDate: invoiceDueDate,
    vendor: invoiceVendor,
    projectId: invoiceProjectId,
    purchaseAccountId: invoicePurchaseAccountId,
    type: invoiceType,
    qboSyncPrevented,
    divisionId: invoiceDivisionId,
    taxCodeId: invoiceTaxCodeId,
    taxCalculation: invoiceTaxCalculation,
    taxPercentage: invoiceTaxPercentage,
  } = {},
  isDisplay,
  mode,
  visible,
  showSaveDestination,
  openDestinationDrawer,
  loading,
  divisionId: propDivisionId, // is defined when accessed through project 'progress' tab or costcode 'progress' tab
  projectId, // is defined when accessed through project 'progress' tab
  costcode, // is defined when accessed through a specific costcode in 'progress' tab
  zIndex,
  invalidDistributionMap,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    connectedToQuickbooks = false,
  } = useSelector((state) => state.settings.company);
  const projects = useSelector((state) => state.projects.projects);
  const forms = useSelector((state) => state.forms.forms);
  const divisions = useSelector((state) => state.settings.divisions);
  const quickbooksIntegrations = useSelector((state) => state.settings.quickbookIntegrations);

  const [linkedFormId, setLinkedFormId] = useState(formId ?? invoiceFormId);
  const [formFilterRange, setFormFilterRange] = useState([
    moment().subtract(1, 'M').startOf('day'),
    moment().endOf('day'),
  ]);
  const [linkedProjectId, setLinkedProjectId] = useState(projectId ?? invoiceProjectId);
  const [purchaseAccounts, setPurchaseAccounts] = useState({});
  const [selectedPurchaseAccount, setSelectedPurchaseAccount] = useState(invoicePurchaseAccountId);
  const [selectedTaxCode, setSelectedTaxCode] = useState(invoiceTaxCodeId);
  const [divisionId, setDivisionId] = useState(propDivisionId ?? invoiceDivisionId);
  const [taxCodesMap, setTaxCodesMap] = useState([]);
  const [selectedTaxCalculation, setSelectedTaxCalculation] = useState(invoiceTaxCalculation);
  const [selectedTaxPercentage, setSelectedTaxPercentage] = useState(invoiceTaxPercentage);

  useEffect(() => {
    const getPurchaseAccounts = async () => {
      try {
        const { data: newPurchaseAccounts = [] } = await axios.get('/quickbooks/accounts/purchase');
        setPurchaseAccounts(newPurchaseAccounts);
      } catch (err) {
        setPurchaseAccounts([]);
      }
    };

    const getTaxCodes = async () => {
      try {
        const { data: newTaxCodes = {} } = await axios.get('/quickbooks/taxcodes');
        setTaxCodesMap(newTaxCodes);
      } catch (err) {
        // silent fail
      }
    };

    if (!visible || !connectedToQuickbooks) return;
    getPurchaseAccounts();
    getTaxCodes();
  }, [visible, connectedToQuickbooks]);

  useEffect(() => {
    if (!formRef.current || !visible) return;
    formRef.current.setFieldsValue({
      type: invoiceType ?? 'purchase',
    });
  }, [formRef, visible, invoiceType]);

  useEffect(() => {
    if (Object.keys(divisions).length === 1) {
      setDivisionId(Object.keys(divisions)[0]);
    }
  }, [divisions]);

  useEffect(() => {
    if (!formRef.current || !visible) return;
    formRef.current.setFieldsValue({
      divisionId,
    });
  }, [visible, formRef.current, divisionId]);

  const projectMap = useMemo(() => getIdMap(projects), [projects]);

  // This is used to determine the divisionId of the invoice on the backend
  const relevantDivisionId = useMemo(() => {
    if (divisionId) return divisionId;

    if (linkedProjectId) {
      const project = projectMap[linkedProjectId];
      return project?.divisionId;
    }

    if (linkedFormId) {
      const form = forms[linkedFormId];
      return form?.divisionId;
    }

    return null;
  }, [linkedFormId, linkedProjectId, forms, projectMap, divisionId]);

  const {
    defaultPurchaseAccount,
    purchaseAccountOptions,
    purchaseAccountOptionsMap,
    defaultTaxCode,
    taxCodeOptions,
    taxCodeOptionsMap,
    defaultTaxCalculation,
  } = useMemo(() => {
    if (!connectedToQuickbooks || !relevantDivisionId) {
      return {
        purchaseAccountOptions: [],
        purchaseAccountOptionsMap: {},
        defaultTaxCode: null,
        taxCodeOptions: [],
        taxCodeOptionsMap: {},
        defaultTaxCalculation: null,
      };
    }

    const options = purchaseAccounts[relevantDivisionId] || [];
    const ourIntegration = quickbooksIntegrations
      .find(({ defaultDivisionId: divId }) => (divId === relevantDivisionId));
    const defaultAccount = options.find(({ IsDefault }) => IsDefault);

    const taxOptions = taxCodesMap?.[divisionId] || [];
    const defaultTax = taxOptions.find(({ isDefault }) => isDefault);

    return {
      defaultPurchaseAccount: defaultAccount,
      purchaseAccountOptions: options,
      purchaseAccountOptionsMap: getIdMap(options, 'Id'),
      defaultTaxCode: defaultTax ?? null,
      taxCodeOptions: taxOptions,
      taxCodeOptionsMap: getIdMap(taxOptions, 'Id'),
      defaultTaxCalculation: ourIntegration?.defaultTaxCalculation ?? null,
    };
  }, [
    connectedToQuickbooks,
    relevantDivisionId,
    purchaseAccounts,
    taxCodesMap,
    quickbooksIntegrations,
  ]);

  useEffect(() => {
    const selectedAccount = purchaseAccountOptionsMap[selectedPurchaseAccount];

    if (!selectedAccount) {
      if (defaultPurchaseAccount) {
        setSelectedPurchaseAccount(defaultPurchaseAccount.Id);
      } else if (selectedPurchaseAccount) {
        setSelectedPurchaseAccount();
      }
    }
  }, [
    defaultPurchaseAccount,
    selectedPurchaseAccount,
    purchaseAccountOptionsMap,
  ]);

  useEffect(() => {
    if (!visible || isDisplay || invoiceTaxPercentage) return;
    const taxPercentage = window.localStorage.getItem(taxPercentageKey);
    if (taxPercentage) {
      setSelectedTaxPercentage(Number(taxPercentage));
    }
  }, [visible, isDisplay, invoiceTaxPercentage]);

  useEffect(() => {
    if (selectedTaxCalculation) return;
    setSelectedTaxCalculation(defaultTaxCalculation);
  }, [defaultTaxCalculation, selectedTaxCalculation]);

  useEffect(() => {
    if (!formRef.current) return;
    formRef.current.setFieldsValue({ taxCalculation: selectedTaxCalculation });
  }, [selectedTaxCalculation]);

  useEffect(() => {
    if (!formRef.current || !projectId || !invoiceId) return;
    formRef.current.setFieldsValue({ taxPercentage: selectedTaxPercentage });
  }, [selectedTaxPercentage, projectId, invoiceId]);

  useEffect(() => {
    if (!formRef.current) return;
    const selectedAccount = purchaseAccountOptionsMap[selectedPurchaseAccount];

    formRef.current.setFieldsValue({
      purchaseAccount: selectedPurchaseAccount ?? null,
      purchaseAccountType: selectedAccount?.AccountType ?? null,
    });
  }, [selectedPurchaseAccount, purchaseAccountOptionsMap]);

  useEffect(() => {
    const selectedTax = taxCodeOptionsMap[selectedTaxCode];
    if (!selectedTax) {
      if (defaultTaxCode) {
        setSelectedTaxCode(defaultTaxCode.Id);
      } else if (selectedTaxCode) {
        setSelectedTaxCode();
      }
    }
  }, [
    defaultTaxCode,
    selectedTaxCode,
    taxCodeOptionsMap,
  ]);

  useEffect(() => {
    if (!formRef.current) return;
    const selectedTax = taxCodeOptionsMap[selectedTaxCode];
    formRef.current.setFieldsValue({ taxCode: selectedTax?.Id ?? null });
  }, [selectedTaxCode, taxCodeOptionsMap]);

  const selectedPOLabel = useMemo(() => {
    if (!formId && !invoiceFormId) return '-';
    const {
      formValue,
      projectId: formProjectId,
      number,
      templateName,
      createdAt: date,
      type,
    } = forms[formId ?? invoiceFormId] || {};
    const { name: projectName } = projectMap[formProjectId] || {};
    return getValueFormTitle({
      formName: templateName,
      projectName,
      number,
      formValue,
      date,
      type,
      t,
    });
  }, [formId, invoiceFormId, forms, projectMap]);

  const poFormOptions = useMemo(() => {
    const selectOptions = {};
    const filteredForms = Object.values(forms)
      .filter(({ type, isClosed, divisionId: formDivisionId }) => (
        PAYABLE_FORM_TYPES.has(type) && !isClosed && divisionId === formDivisionId
      ));

    filteredForms.sort(sortByNumber('createdAt'));
    filteredForms.forEach(({
      id: filteredFormId,
      number,
      createdAt: date,
      projects: formProjects = [],
      templateName,
      formValue,
      type,
    }) => {
      formProjects.forEach((formProjectId) => {
        const { name: projectName } = projectMap[formProjectId] || {};
        if (!filteredFormId || !formProjectId) return;
        if (!(type in selectOptions)) selectOptions[type] = {};
        if (!(formProjectId in selectOptions[type])) {
          selectOptions[type][formProjectId] = {
            selectable: false,
            value: formProjectId,
            title: projectName,
            children: [],
          };
        }
        selectOptions[type][formProjectId].children.push({
          isLeaf: true,
          value: filteredFormId,
          title: getValueFormTitle({
            projectName,
            formName: templateName,
            number,
            date,
            formValue,
            type,
            t,
          }),
        });
      });
    });
    return Object.keys(selectOptions).map((type) => {
      const formProjectMap = selectOptions[type];
      return {
        selectable: false,
        value: type,
        title: type,
        children: Object.values(formProjectMap),
      };
    });
  }, [projectMap, forms, divisionId]);

  const treeSelectRender = useCallback((menu) => (
    <div style={{ width: '100%', padding: 5 }}>
      <Row align="middle" gutter={[8, 0]}>
        <Col span={12}>
          <Text style={{ display: 'flex', justifyContent: 'flex-end' }}>Select date range:</Text>
        </Col>
        <Col span={12}>
          <RangePicker
            autoFocus
            allowClear={false}
            style={{ width: '100%' }}
            format="MMM Do YY"
            disabledDate={(current) => current > DateTime.local().endOf('day').toMillis()}
            value={formFilterRange}
            onChange={setFormFilterRange}
            popupStyle={{ zIndex: 1000, position: 'fixed' }}
            getPopupContainer={(node) => node.parentNode}
          />
        </Col>
      </Row>
      <Divider style={{ margin: '4px 0px 8px 0px' }} />
      {menu}
    </div>
  ), [formFilterRange]);

  const qboSyncText = useMemo(() => {
    if (qboSyncPrevented) {
      return (
        <div style={{ marginBottom: 10 }}>
          <b>
            QuickBooks syncing was manually disabled for this invoice
          </b>
        </div>
      );
    }

    return null;
  }, [qboSyncPrevented]);

  useEffect(() => {
    if (visible) {
      setLinkedFormId(formId ?? invoiceFormId);
      setLinkedProjectId(projectId ?? invoiceProjectId);
      setSelectedPurchaseAccount(invoicePurchaseAccountId);
      setDivisionId(propDivisionId ?? invoiceDivisionId);
      setSelectedTaxCode(invoiceTaxCodeId);
      setSelectedTaxCalculation(invoiceTaxCalculation);
      setSelectedTaxPercentage(invoiceTaxPercentage);
    } else {
      setLinkedFormId();
      setFormFilterRange([
        moment().subtract(1, 'M').startOf('day'),
        moment().endOf('day'),
      ]);
      setLinkedProjectId();
      setSelectedPurchaseAccount();
      setDivisionId();
      setSelectedTaxCode();
      setSelectedTaxCalculation();
      setSelectedTaxPercentage();
    }
  }, [visible]);

  useEffect(() => {
    const query = getValueFormsQuery({
      range: formFilterRange,
      formId: formId ?? invoiceFormId,
    });
    dispatch(getForms(query));
  }, [formId, invoiceFormId, formFilterRange]);

  useEffect(() => {
    if (visible) {
      dispatch(getQuickbooksIntegrations());
    }
  }, [visible]);

  return (
    <Form
      ref={formRef}
      layout="vertical"
      style={{ width: '100%' }}
    >
      {isDisplay && qboSyncText}
      <FormTextInput
        isNotDisplay={!isDisplay}
        label="Invoice Number"
        name="invoiceNumber"
        value={invoiceNumber}
        rules={[{ required: true, message: 'Please input invoice number' }]}
      />
      <FormCurrencyInput
        isNotDisplay={!isDisplay}
        label="Invoice Amount"
        name="invoiceAmount"
        value={getFormattedCurrency(invoiceAmount)}
      />
      <FormTextInput
        isNotDisplay={!isDisplay}
        label="Invoice Description"
        name="invoiceDescription"
        value={invoiceDescription || '-'}
      />
      <FormTextInput
        isNotDisplay={!isDisplay}
        label="Notes"
        name="invoiceNotes"
        value={invoiceNotes || '-'}
        textarea
        autoSize={{ minRows: 2, maxRows: 6 }}
        supportMultiLine
        textProps={{
          maxLength: INVOICE_NOTES_MAX_LENGTH,
        }}
      />
      {!isDisplay && (
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => (
            prevValues.invoiceNotes !== currentValues.invoiceNotes
          )}
        >
          {({ getFieldValue }) => (
            <div
              style={{
                textAlign: 'right',
                fontSize: 12,
                color: 'rgba(0, 0, 0, 0.45)',
                marginTop: -4,
              }}
            >
              {getFieldValue('invoiceNotes')?.length ?? 0}
              {' / '}
              {INVOICE_NOTES_MAX_LENGTH}
            </div>
          )}
        </Form.Item>
      )}
      <Row gutter={8}>
        <Col span={12}>
          <OnTraccrSimpleDatePicker
            isNotDisplay={!isDisplay}
            label="Date Issued"
            name="invoiceIssueDate"
            value={getFormattedDate(invoiceDateIssued)}
            allowFutureDates={false}
            rules={[{ required: true, message: 'Please specify invoice issue date' }]}
          />
        </Col>
        <Col span={12}>
          <OnTraccrSimpleDatePicker
            isNotDisplay={!isDisplay}
            label="Due Date"
            name="invoiceDueDate"
            value={getFormattedDate(invoiceDueDate)}
            allowFutureDates
          />
        </Col>
      </Row>
      <Form.Item
        name="divisionId"
        label="Division"
        style={{ marginTop: 10, marginBottom: 10 }}
      >
        <DivisionSelector
          displayMode={isDisplay}
          divisionId={divisionId}
          onChange={setDivisionId}
          disabled={propDivisionId || invoiceDivisionId}
        />
      </Form.Item>
      <FormTreeSelectInput
        isNotDisplay={!isDisplay}
        label="Payable Form"
        name="formId"
        value={selectedPOLabel}
        formLabelStyle={{
          style: {
            marginTop: 10,
          },
        }}
        treeSelectOptions={{
          showSearch: true,
          allowClear: true,
          // Dont allow PO Link modification when accessed through PO form detail slider
          disabled: formId || invoiceFormId || linkedProjectId || !divisionId,
          value: linkedFormId,
          treeNodeFilterProp: 'title',
          treeData: poFormOptions,
          placeholder: 'Select a PO',
          dropdownRender: treeSelectRender,
          onChange: setLinkedFormId,
        }}
      />
      <ContactSelector
        isNotDisplay={!isDisplay}
        formRef={formRef}
        contact={invoiceVendor ?? '-'}
        isVendor
        formStyle={{ marginBottom: -50 }}
        formLabelStyle={{
          style: {
            marginTop: 10,
          },
        }}
        zIndex={zIndex + 1}
        shouldFilterByDivision
        divisionId={divisionId}
      />
      <InvoiceStatusSelector
        visible={visible}
        formRef={formRef}
        isNotDisplay={!isDisplay}
        status={invoiceStatus || '-'}
        formStyle={{
          marginBottom: 10,
        }}
      />
      {!!connectedToQuickbooks && (
        <>
          <Form.Item
            name="purchaseAccount"
            label="Purchase Account"
            style={{
              marginBottom: 10,
            }}
          >
            { !isDisplay ? (
              <Select
                disabled={!divisionId}
                style={{ width: '100%' }}
                placeholder="Select a purchase account"
                optionFilterProp="label"
                value={selectedPurchaseAccount}
                onChange={setSelectedPurchaseAccount}
                options={purchaseAccountOptions.map(({ Id, Name }) => ({ label: Name, value: Id }))}
              />
            ) : (
              <DisplayText
                title={purchaseAccountOptionsMap[selectedPurchaseAccount]?.Name ?? '-'}
              />
            )}
          </Form.Item>
          <Form.Item
            name="taxCode"
            label="Tax Code"
            style={{ marginBottom: 10 }}
          >
            {!isDisplay ? (
              <Select
                disabled={!divisionId}
                style={{ width: '100%' }}
                placeholder="Select a tax code"
                optionFilterProp="label"
                value={selectedTaxCode}
                onChange={setSelectedTaxCode}
                options={taxCodeOptions.map(({ Id, Name }) => ({ value: Id, label: Name }))}
              />
            ) : <DisplayText title={taxCodeOptionsMap[selectedTaxCode]?.Name ?? '-'} />}
          </Form.Item>
          <Form.Item
            name="taxCalculation"
            label="Tax Calculation"
            style={{ marginBottom: 10 }}
          >
            {!isDisplay ? (
              <Select
                disabled={!divisionId}
                style={{ width: '100%' }}
                placeholder="Select a tax calculation"
                optionFilterProp="label"
                options={TAX_CALCULATION_OPTIONS}
                value={selectedTaxCalculation}
                onChange={setSelectedTaxCalculation}
              />
            ) : (
              <DisplayText title={TAX_CALCULATION_OPTIONS.find(({ value }) => value === selectedTaxCalculation)?.label ?? '-'} />
            )}
          </Form.Item>
          <Form.Item
            name="type"
            label="Type"
            style={{
              marginBottom: 10,
            }}
          >
            { !isDisplay ? (
              <Select
                style={{ width: '100%' }}
                placeholder="Select a type"
                optionFilterProp="label"
                options={TypeOptions}
                disabled={invoiceId}
              />
            ) : (
              <DisplayText
                title={TypeOptions.find(({ value }) => value === invoiceType)?.label ?? 'Expense'}
              />
            )}
          </Form.Item>
          {!isDisplay && (
            <Form.Item
              name="qboSyncPrevented"
              style={{ marginBottom: 10 }}
            >
              <OnTraccrCheckbox
                disabled={invoiceId}
                label="Disable QuickBooks Sync"
              />
            </Form.Item>
          )}
        </>
      )}
      {showSaveDestination
      && (
        <Form.Item
          name="filePath"
          valuePropName="filePath"
          style={{ marginTop: 10, marginBottom: 10, color: 'rgba(0, 0, 0, 0.85)' }}
        >
          <FileDestinationSelect
            isDisplay={isDisplay}
            openDestinationDrawer={openDestinationDrawer}
          />
        </Form.Item>
      )}
      { projectId && invoiceId && (
        <Form.Item
          name="taxPercentage"
          label="Tax Percentage"
          style={{ marginBottom: 10 }}
        >
          {!isDisplay ? (
            <OnTraccrNumberInput
              style={{ width: '100%' }}
              placeholder="Enter tax percentage"
              min={0}
              max={100}
              step={0.01}
              precision={2}
              value={selectedTaxPercentage}
              onChange={(value) => {
                setSelectedTaxPercentage(value);
                window.localStorage.setItem(taxPercentageKey, value);
              }}
            />
          ) : (
            <DisplayText title={`${selectedTaxPercentage || 0}%`} />
          )}
        </Form.Item>
      )}
      <InvoiceAmountDistributor
        formRef={formRef}
        formId={linkedFormId}
        invoiceId={invoiceId}
        isDisplay={isDisplay}
        mode={mode}
        visible={visible}
        costcode={costcode}
        projectId={linkedProjectId}
        divisionId={divisionId}
        invalidDistributionMap={invalidDistributionMap}
      />
      {loading
        && (
          <Row justify="center" align="middle" className="form-loading-container">
            <Spin size="large" />
          </Row>
        )}
    </Form>
  );
}

/* eslint-disable react/forbid-prop-types */
InvoiceAddForm.propTypes = {
  formRef: PropTypes.object.isRequired,
  formId: PropTypes.string,
  invoice: PropTypes.shape(INVOICE_PROP),
  isDisplay: PropTypes.bool.isRequired,
  mode: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  openDestinationDrawer: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  showSaveDestination: PropTypes.bool,
  projectId: PropTypes.string,
  costcode: PropTypes.object,
  zIndex: PropTypes.number,
  invalidDistributionMap: PropTypes.shape({}),
  divisionId: PropTypes.string,
};

InvoiceAddForm.defaultProps = {
  formId: null,
  invoice: {},
  loading: false,
  showSaveDestination: false,
  projectId: null,
  costcode: undefined,
  zIndex: 1000,
  invalidDistributionMap: {},
  divisionId: null,
};

export default InvoiceAddForm;
